// Heading extraction + id stamping for the table of contents. Pure and shared between the
// Nitro server (stamps ids into the article payload once, cached) and the browser (reads
// the same ids back for the TOC list), so the two can never drift and there is no
// render-order coupling.

// The rich-text bodies the TOC reads, in the order their headings de-dupe. `getTocRichTexts`
// mirrors the sources the old client-side DOM scrape saw: article-text bloks in
// content/sidebar/body, plus the restaurant `text` field.

function textOf(node) {
    if (!node?.content) {
        return ''
    }
    return node.content
        .map(child => (child.type === 'text' ? (child.text ?? '') : textOf(child)))
        .join('')
        .replace(/\s+/g, ' ')
        .trim()
}

// Never returns an empty string — an un-sluggable heading still needs a stable anchor, and
// the resolver must never emit a bare id. The expression is kept byte-for-byte identical
// to the slug the TOC used to compute client-side so existing fragment links keep
// resolving.
export function slugifyHeading(text = '') {
    const slug = String(text).toLowerCase().replace(/ /g, '-').replace(/[^a-z0-9-]/gi, '')
    return slug || 'section'
}

// The rich-text documents (content/sidebar/body article-text bloks + the free-form `text`
// field) the TOC draws headings from. Order matters: it is the de-dup order.
export function getTocRichTexts(content) {
    if (!content) {
        return []
    }
    const docs = []
    for (const arr of [content.content, content.sidebar, content.body]) {
        for (const blok of arr ?? []) {
            if (blok?.component === 'article-text') {
                docs.push(blok.text)
            }
        }
    }
    if (content.text) {
        docs.push(content.text)
    }
    return docs.filter(doc => doc && Array.isArray(doc.content))
}

// Visits every heading node in doc order. CB receives (node, level, ownTitle, ownId) for
// each; ownId is the de-duped id. Returns the list for the caller's convenience.
function walkHeadings(docs, cb) {
    const slugCounts = new Map()
    const out = []
    for (const doc of docs) {
        for (const node of doc.content) {
            if (node.type !== 'heading') {
                continue
            }
            const level = node.attrs?.level
            if (level !== 2 && level !== 3) {
                continue
            }
            const title = textOf(node)
            const base = slugifyHeading(title)
            const seen = slugCounts.get(base) ?? 0
            const id = seen === 0 ? base : `${base}-${seen}`
            slugCounts.set(base, seen + 1)
            const entry = { node, level, title, id }
            out.push(entry)
            cb(entry)
        }
    }
    return out
}

// Rich-text `heading` node resolver that turns a stamped `attrs.id` into the emitted
// element's id and leaves every other heading untouched. Pass `schema: { nodes: {
// ...RichTextSchema.nodes, heading: tocHeadingResolver }, marks: RichTextSchema.marks }`
// at each renderRichText call site (the default schema spreads in so paragraphs/lists/etc.
// still render). Kept import-free — RichTextSchema comes from the `@storyblok/vue`
// auto-import, which only resolves inside the app, not bare node.
export function tocHeadingResolver(node) {
    const level = node.attrs?.level
    const id = node.attrs?.id
    if ((level === 2 || level === 3) && id) {
        return { tag: [{ tag: `h${level}`, attrs: { id } }] }
    }
    return { tag: `h${level}` }
}

// Writes the de-duped ids onto the heading nodes' attrs (mutates the passed content) so
// renderRichText emits them into the SSR HTML. Runs in the Nitro article handler, on the
// raw story, before the payload is cached.
export function stampHeadingIds(content) {
    walkHeadings(getTocRichTexts(content), ({ node, id }) => {
        node.attrs = { ...node.attrs, id }
    })
}

// The TOC's heading list, read back from the (already id-stamped) content. h2s win if any
// exist — the old `.article__content:not(:has(...))` fallback, applied over the whole
// story rather than per container — otherwise h3s.
export function getTocHeadings(content) {
    const all = walkHeadings(getTocRichTexts(content), () => {})
    const h2s = all.filter(h => h.level === 2)
    const list = h2s.length ? h2s : all.filter(h => h.level === 3)
    return list.map(({ id, level, title }) => ({ id, level, title }))
}
